/**
 * agentGatewayClient — SEC-14: Agent Gateway Intent Client.
 *
 * Browser-side wrapper that calls the declareAndVerifyIntent Cloud Function.
 *
 * KTRUST-L1 fire-and-forget pattern:
 *   Callers should NOT await this function. Intent declaration is a parallel
 *   sidecar — it never blocks the governed AI call.
 *
 * Usage (in sentinelService or governed call wrappers):
 *
 *   import { declareIntent } from './agentGatewayClient';
 *
 *   // Fire-and-forget — do not await
 *   declareIntent({ tenantId, agentId, callType, module, decisionType, intentSummary })
 *     .catch(err => console.warn('[agentGatewayClient] intent declaration failed (non-fatal):', err.message));
 *
 * Returns Promise<{ intentId, status, verifiedAt }> — only used by callers
 * who explicitly need the intentId for correlation. Most callers discard it.
 *
 * Architecture rules:
 *   - No direct Firebase imports here beyond httpsCallable + functions instance
 *   - All governed writes server-side via the CF
 *   - tenantId required on every call
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';

const GATEWAY_CF_NAME = 'declareAndVerifyIntent';

let _declareAndVerifyIntent = null;

function _getCallable() {
  if (!_declareAndVerifyIntent) {
    _declareAndVerifyIntent = httpsCallable(functions, GATEWAY_CF_NAME);
  }
  return _declareAndVerifyIntent;
}

/**
 * Declare agent intent to the gateway service.
 *
 * @param {object} opts
 * @param {string} opts.tenantId       - Required: tenant context
 * @param {string} opts.agentId        - Required: agent identifier (e.g. 'aegis', 'artemis-selene')
 * @param {string} opts.callType       - Required: 'aegis' | 'artemis' | 'council' | 'gateway' | 'internal'
 * @param {string} opts.module         - Required: calling module name
 * @param {string} opts.decisionType   - Required: decision type slug
 * @param {string} opts.intentSummary  - Required: non-empty human-readable intent description
 *
 * @returns {Promise<{ intentId: string, status: 'verified'|'rejected', verifiedAt: string }>}
 */
export async function declareIntent(opts) {
  const { tenantId, agentId, callType, module: mod, decisionType, intentSummary } = opts || {};

  if (!tenantId) {
    throw new Error('[agentGatewayClient] tenantId is required');
  }

  const callable = _getCallable();
  const result = await callable({
    tenantId,
    agentId,
    callType,
    module: mod,
    decisionType,
    intentSummary,
  });

  return result.data;
}

export default { declareIntent };
